import { getApprovedProofSnippet } from "../src/guardrails";
import { FlowConfig, GemSalesConfig } from "../src/types";

const shilaSourceUrl = "/products/shilasource-himalayan-shilajit-resin";
const gummiesUrl = "/products/himalayan-shilajit-gummies";
const fusiForceUrl = "/products/fusiforce-creatine-gummies";
const menoMateUrl = "/products/menomate";

function resolveCartAddOnHandle(goal: string | undefined): string | null {
  const addOns: Record<string, string> = {
    energy: "himalayan-shilajit-gummies",
    recovery: "fusiforce-creatine-gummies",
    convenience: "himalayan-shilajit-gummies",
    women: "menomate",
  };

  return addOns[goal ?? ""] ?? "himalayan-shilajit-gummies";
}

function resolveStackHandle(goal: string | undefined): string {
  const stacks: Record<string, string> = {
    energy: "himalayan-shilajit-gummies",
    convenience: "himalayan-shilajit-gummies",
    recovery: "fusiforce-creatine-gummies",
    women: "menomate",
  };

  return stacks[goal ?? ""] ?? "himalayan-shilajit-gummies";
}

const flows: FlowConfig[] = [
  {
    id: "wn-homepage-goal-router-v1",
    title: "Find your Wellness Nest fit",
    templateId: "wellness_product_fit_result_v1",
    steps: [
      {
        id: "goal",
        question: "What are you shopping for today?",
        options: [
          { id: "energy", label: "More daily energy", intentLabel: "energy" },
          { id: "recovery", label: "Strength and recovery", intentLabel: "recovery" },
          { id: "vitality", label: "Men's vitality", intentLabel: "vitality" },
          { id: "women", label: "Women's wellness", intentLabel: "women_wellness" },
          { id: "proof", label: "Lab-tested supplements", intentLabel: "proof" },
        ],
      },
      {
        id: "priority",
        question: "What matters most?",
        options: [
          { id: "strongest", label: "Strongest formula" },
          { id: "easy", label: "Easiest habit" },
          { id: "taste", label: "Best taste" },
          { id: "proof", label: "Best proof" },
          { id: "value", label: "Best value" },
        ],
      },
      {
        id: "concern",
        question: "Any concern before choosing?",
        options: [
          { id: "safety", label: "Safety/lab testing" },
          { id: "price", label: "Price" },
          { id: "taste", label: "Taste" },
          { id: "timeline", label: "How long it takes" },
          { id: "unsure", label: "Not sure" },
        ],
      },
    ],
    resolveResult: (answers, config) => {
      if (answers.goal === "recovery") {
        return {
          title: "FusiForce is the clearer starting point",
          body: "Your answers point to strength and recovery. Start there, then compare Shilajit if your main goal shifts toward energy or vitality.",
          ctaLabel: "View FusiForce",
          ctaUrl: fusiForceUrl,
          recommendedProductHandle: "fusiforce-creatine-gummies",
        };
      }

      if (answers.goal === "women") {
        return {
          title: "Start with the women's wellness range",
          body: "Your goal is more specific than a general Shilajit recommendation. Review Wellness Nest's women's wellness products before choosing a supplement.",
          ctaLabel: "Shop best sellers",
          ctaUrl: "/#shopify-section-template--best-sellers",
        };
      }

      if (answers.priority === "taste" || answers.priority === "easy" || answers.concern === "taste") {
        return {
          title: "Compare gummies before choosing resin",
          body: "If taste and convenience matter most, gummies may fit your routine better than pure resin. If potency and proof matter most, ShilaSource resin is worth reviewing.",
          ctaLabel: "Compare ShilaSource",
          ctaUrl: shilaSourceUrl,
          recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
          secondaryLabel: "View gummies",
          secondaryUrl: gummiesUrl,
        };
      }

      return {
        title: "ShilaSource is likely the right first product to review",
        body: "Your answers point toward energy, focus, vitality, or proof-led shopping. Check the ShilaSource page and review the lab proof before deciding.",
        ctaLabel: "View ShilaSource",
        ctaUrl: shilaSourceUrl,
        recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
        secondaryLabel: "Explain proof",
        secondaryUrl: shilaSourceUrl,
        caution: config.approvedCopy.safetyCaveat,
      };
    },
  },
  {
    id: "wn-shilasource-fit-v1",
    title: "ShilaSource fit check",
    templateId: "wellness_product_fit_result_v1",
    steps: [
      {
        id: "goal",
        question: "What do you want ShilaSource to support most?",
        options: [
          { id: "energy", label: "Energy" },
          { id: "focus", label: "Focus" },
          { id: "vitality", label: "Vitality" },
          { id: "recovery", label: "Recovery" },
          { id: "wellness", label: "General wellness" },
        ],
      },
      {
        id: "format",
        question: "How do you feel about resin?",
        options: [
          { id: "resin_ok", label: "Fine mixing it into tea/coffee" },
          { id: "strongest", label: "I want the strongest format" },
          { id: "easy", label: "I prefer something easy/tasty" },
          { id: "unsure", label: "Not sure" },
        ],
      },
      {
        id: "hesitation",
        question: "What is your biggest hesitation?",
        options: [
          { id: "real", label: "Is it real?" },
          { id: "safe", label: "Is it safe?" },
          { id: "taste", label: "Will I like the taste?" },
          { id: "price", label: "Is it worth the price?" },
          { id: "medication", label: "I take medication" },
        ],
      },
    ],
    resolveResult: (answers, config) => {
      if (answers.hesitation === "medication" || answers.hesitation === "safe") {
        return {
          title: "Check safety before choosing",
          body: "ShilaSource is positioned as a lab-tested Shilajit resin, but product fit is different from medical suitability.",
          ctaLabel: "Review lab proof",
          ctaUrl: shilaSourceUrl,
          recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
          caution: config.approvedCopy.safetyCaveat,
        };
      }

      if (answers.format === "easy" || answers.hesitation === "taste") {
        return {
          title: "Compare resin with gummies",
          body: "Pure resin can be a better fit for potency and proof-led shoppers. Gummies may be easier if taste and convenience are your main concern.",
          ctaLabel: "Stay on ShilaSource",
          ctaUrl: shilaSourceUrl,
          recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
          secondaryLabel: "View gummies",
          secondaryUrl: gummiesUrl,
        };
      }

      return {
        title: "ShilaSource looks like a strong fit",
        body: "Your answers point to a shopper who values resin format, proof, and a wellness routine. Review the lab proof and guarantee before adding it to cart.",
        ctaLabel: "Add or review ShilaSource",
        ctaUrl: shilaSourceUrl,
        recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
        secondaryLabel: "Explain lab proof",
        secondaryUrl: shilaSourceUrl,
      };
    },
  },
  {
    id: "wn-proof-explainer-v1",
    title: "Lab proof explainer",
    templateId: "wellness_proof_explainer_v1",
    steps: [
      {
        id: "proof_topic",
        question: "What do you want explained?",
        options: [
          { id: "heavy_metals", label: "Heavy metals" },
          { id: "fulvic", label: "Fulvic acid" },
          { id: "coa", label: "Certificate of Analysis" },
          { id: "fake", label: "Fake Shilajit risk" },
          { id: "limits", label: "What this does not prove" },
        ],
      },
    ],
    resolveResult: (answers, config) => ({
      title: "Proof should reduce uncertainty, not make medical promises",
      body: getApprovedProofSnippet(answers.proof_topic, config),
      ctaLabel: "Continue reviewing ShilaSource",
      ctaUrl: shilaSourceUrl,
      recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
      caution: config.approvedCopy.safetyCaveat,
    }),
  },
  {
    id: "wn-shilajit-format-comparator-v1",
    title: "Shilajit format comparator",
    templateId: "wellness_format_comparator_v1",
    steps: [
      {
        id: "priority",
        question: "What matters most for your daily Shilajit habit?",
        options: [
          { id: "proof", label: "Proof and potency" },
          { id: "taste", label: "Taste" },
          { id: "convenience", label: "Convenience" },
          { id: "travel", label: "Travel" },
        ],
      },
      {
        id: "routine",
        question: "How would you prefer to take it?",
        options: [
          { id: "mix", label: "Mix into tea or coffee" },
          { id: "chew", label: "Chew something quick" },
          { id: "unsure", label: "Not sure yet" },
        ],
      },
    ],
    resolveResult: (answers, config) => {
      if (answers.priority === "taste" || answers.priority === "convenience" || answers.priority === "travel" || answers.routine === "chew") {
        return {
          title: "Gummies may fit your habit better",
          body: "Your answers point toward taste or convenience. Gummies may be easier to repeat daily, while resin is still worth comparing if proof and traditional format matter more.",
          ctaLabel: "View gummies",
          ctaUrl: gummiesUrl,
          recommendedProductHandle: "himalayan-shilajit-gummies",
          secondaryLabel: "Compare ShilaSource resin",
          secondaryUrl: shilaSourceUrl,
          caution: config.approvedCopy.safetyCaveat,
        };
      }

      return {
        title: "ShilaSource resin is the clearer format to review",
        body: "Your answers point toward proof, potency positioning, or comfort mixing resin into a daily drink. Review the lab proof and taste expectations before choosing.",
        ctaLabel: "View ShilaSource resin",
        ctaUrl: shilaSourceUrl,
        recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
        secondaryLabel: "View gummies",
        secondaryUrl: gummiesUrl,
        caution: config.approvedCopy.safetyCaveat,
      };
    },
  },
  {
    id: "wn-shilasource-objection-resolver-v1",
    title: "ShilaSource objection resolver",
    templateId: "wellness_objection_resolver_v1",
    steps: [
      {
        id: "objection",
        question: "What is holding you back?",
        options: [
          { id: "taste", label: "Taste" },
          { id: "value", label: "Price/value" },
          { id: "timeline", label: "How fast it works" },
          { id: "medication", label: "Medication or safety" },
          { id: "guarantee", label: "Guarantee" },
        ],
      },
    ],
    resolveResult: (answers, config) => {
      if (answers.objection === "medication") {
        return {
          title: "Resolve safety before buying",
          body: config.approvedCopy.medicationSafetyResponse,
          ctaLabel: "Review lab proof",
          ctaUrl: shilaSourceUrl,
          recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
          caution: config.approvedCopy.safetyCaveat,
        };
      }

      if (answers.objection === "taste") {
        return {
          title: "Taste may decide the format",
          body: config.approvedCopy.tasteResponse,
          ctaLabel: "Compare gummies",
          ctaUrl: gummiesUrl,
          recommendedProductHandle: "himalayan-shilajit-gummies",
          secondaryLabel: "Review resin",
          secondaryUrl: shilaSourceUrl,
        };
      }

      if (answers.objection === "value" || answers.objection === "guarantee") {
        return {
          title: "Decide value before checkout",
          body: config.approvedCopy.valueResponse,
          ctaLabel: "Review ShilaSource",
          ctaUrl: shilaSourceUrl,
          recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
        };
      }

      if (answers.objection === "timeline") {
        return {
          title: "Use a realistic expectation window",
          body: "A supplement page can explain routine fit and what customers commonly ask, but it should not promise a guaranteed timeline or medical outcome.",
          ctaLabel: "Review ShilaSource",
          ctaUrl: shilaSourceUrl,
          recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
          caution: config.approvedCopy.safetyCaveat,
        };
      }

      return {
        title: "Resolve the main concern first",
        body: config.approvedCopy.fallbackProofResponse,
        ctaLabel: "Review ShilaSource",
        ctaUrl: shilaSourceUrl,
        recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
        caution: config.approvedCopy.safetyCaveat,
      };
    },
  },
  {
    id: "wn-shilasource-routine-timeline-v1",
    title: "ShilaSource routine timeline",
    templateId: "wellness_routine_timeline_v1",
    steps: [
      {
        id: "goal",
        question: "What routine goal should this support?",
        options: [
          { id: "energy", label: "Daily energy" },
          { id: "focus", label: "Focus" },
          { id: "wellness", label: "General wellness" },
          { id: "habit", label: "Build a consistent habit" },
        ],
      },
      {
        id: "routine",
        question: "Where would it fit best?",
        options: [
          { id: "morning", label: "Morning drink" },
          { id: "work", label: "Workday routine" },
          { id: "gym", label: "Gym routine" },
          { id: "unsure", label: "Not sure" },
        ],
      },
    ],
    resolveResult: (answers, config) => ({
      title: "Build the routine before judging the result",
      body: "Use the first month to evaluate habit fit, taste, timing, proof, and value. This guide should set expectations without promising a guaranteed health outcome.",
      ctaLabel: "Review ShilaSource",
      ctaUrl: shilaSourceUrl,
      recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
      caution: config.approvedCopy.safetyCaveat,
    }),
  },
  {
    id: "wn-shilasource-stack-builder-v1",
    title: "ShilaSource stack builder",
    templateId: "wellness_stack_builder_v1",
    steps: [
      {
        id: "goal",
        question: "What should the add-on support?",
        options: [
          { id: "energy", label: "Energy/focus" },
          { id: "recovery", label: "Recovery" },
          { id: "convenience", label: "Convenience/travel" },
          { id: "women", label: "Women's wellness" },
        ],
      },
      {
        id: "preference",
        question: "What matters most for the stack?",
        options: [
          { id: "threshold", label: "Free shipping threshold" },
          { id: "habit", label: "Daily habit" },
          { id: "format", label: "Different format" },
        ],
      },
    ],
    resolveResult: (answers, config) => {
      const stackHandle = resolveStackHandle(answers.goal);
      const product = config.products[stackHandle];
      if (product) {
        return {
          title: `Pair ShilaSource with ${product.title}`,
          body: `This add-on is selected from merchant-approved pilot rules for the selected goal. Use it to compare habit fit, format, and cart value before checkout.`,
          ctaLabel: `Review ${product.title}`,
          ctaUrl: `${product.url}?gemsales_stack_goal=${answers.goal ?? "default"}`,
          recommendedProductHandle: product.handle,
          secondaryLabel: "Review ShilaSource",
          secondaryUrl: shilaSourceUrl,
          caution: config.approvedCopy.safetyCaveat,
        };
      }

      return {
        title: "Review ShilaSource alone first",
        body: config.approvedCopy.guaranteeSummary,
        ctaLabel: "Review ShilaSource",
        ctaUrl: shilaSourceUrl,
        recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
        caution: config.approvedCopy.safetyCaveat,
      };
    },
  },
  {
    id: "wn-advertorial-story-router-v1",
    title: "Story-to-product guide",
    templateId: "wellness_product_fit_result_v1",
    steps: [
      {
        id: "story_angle",
        question: "What made you keep reading?",
        options: [
          { id: "fake", label: "Fake Shilajit risk" },
          { id: "proof", label: "Lab proof" },
          { id: "energy", label: "Energy/focus" },
          { id: "price", label: "Is it worth it?" },
        ],
      },
      {
        id: "buying_stage",
        question: "Where are you in the decision?",
        options: [
          { id: "skim", label: "Just skimming" },
          { id: "compare", label: "Comparing options" },
          { id: "ready", label: "Almost ready" },
        ],
      },
    ],
    resolveResult: (answers, config) => {
      if (answers.story_angle === "price") {
        return {
          title: "Decide value by proof and routine fit",
          body: config.approvedCopy.valueResponse,
          ctaLabel: "Review ShilaSource",
          ctaUrl: shilaSourceUrl,
          recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
          secondaryLabel: "Compare gummies",
          secondaryUrl: gummiesUrl,
        };
      }

      if (answers.story_angle === "energy") {
        return {
          title: "Move from story to product fit",
          body: "If your main interest is energy or focus, review ShilaSource's format, proof, and safety boundaries before choosing.",
          ctaLabel: "Check ShilaSource fit",
          ctaUrl: shilaSourceUrl,
          recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
          caution: config.approvedCopy.safetyCaveat,
        };
      }

      const proofTopic = answers.story_angle === "fake" ? "fake" : "coa";
      return {
        title: "Use the proof before deciding",
        body: getApprovedProofSnippet(proofTopic, config),
        ctaLabel: "Review ShilaSource proof",
        ctaUrl: shilaSourceUrl,
        recommendedProductHandle: "shilasource-himalayan-shilajit-resin",
        secondaryLabel: "Explain lab proof",
        secondaryUrl: shilaSourceUrl,
        caution: config.approvedCopy.safetyCaveat,
      };
    },
  },
  {
    id: "wn-cart-addon-v1",
    title: "Cart add-on helper",
    templateId: "wellness_cart_addon_v1",
    steps: [
      {
        id: "goal",
        question: "Want an add-on that matches your goal?",
        options: [
          { id: "energy", label: "Energy/focus" },
          { id: "recovery", label: "Recovery" },
          { id: "convenience", label: "Convenience" },
          { id: "women", label: "Women's wellness" },
          { id: "no", label: "No thanks" },
        ],
      },
    ],
    resolveResult: (answers, config) => {
      if (answers.goal === "no") {
        return {
          title: "No add-on needed",
          body: config.approvedCopy.guaranteeSummary,
          ctaLabel: "Continue checkout",
          ctaUrl: "/checkout",
        };
      }

      const addOnHandle = resolveCartAddOnHandle(answers.goal);
      const addOn = addOnHandle ? config.products[addOnHandle] : null;
      if (addOn) {
        return {
          title: `Add ${addOn.title} for this goal`,
          body: `Free shipping starts at ${config.commerce.currency} ${config.commerce.freeShippingThreshold}. This add-on matches the selected goal and should be shown only when it fits current merchant-approved cart rules.`,
          ctaLabel: `Review ${addOn.title}`,
          ctaUrl: `${addOn.url}?gemsales_addon=cart_goal_${answers.goal}`,
          recommendedProductHandle: addOn.handle,
        };
      }

      return {
        title: "Use the guarantee before checkout",
        body: config.approvedCopy.guaranteeSummary,
        ctaLabel: "Review best sellers",
        ctaUrl: "/",
      };
    },
  },
];

export const wellnessNestConfig: GemSalesConfig = {
  shopId: "wellnessnest",
  brandName: "Wellness Nest",
  mode: "pilot",
  theme: {
    primaryColor: "#b4232f",
    primaryHoverColor: "#8f1d28",
    primaryTintColor: "#fff1f2",
    textColor: "#221f1f",
    mutedTextColor: "#6D7175",
    backgroundColor: "#ffffff",
    surfaceAltColor: "#F6F6F7",
    borderColor: "#e5ded8",
    borderSubduedColor: "#C9CCCF",
    successColor: "#008060",
    warningColor: "#916A00",
    criticalColor: "#D72C0D",
    infoColor: "#005BD3",
    radiusSm: "6px",
    radiusMd: "8px",
    radiusLg: "12px",
    shadowSm: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
    shadowMd: "0 4px 12px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)",
    fontBody: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontHeading: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  commerce: {
    currency: "USD",
    freeShippingThreshold: 70,
  },
  triggers: [
    {
      id: "wn-advertorial-story-companion",
      runtimeSlotId: "ai-guided-offer",
      surface: "inline",
      pageTypes: ["advertorial", "article"],
      question: "Want the short version for your buying decision?",
      quickReplies: ["Is it real?", "Show proof", "Is it worth it?", "Choose for me"],
      flowId: "wn-advertorial-story-router-v1",
      priority: 85,
      intentTargets: ["lab_proof", "trust_safety", "price_value", "energy_focus", "product_fit"],
      maxImpressionsPerSession: 1,
      anchorSelectors: ["[data-gemstory-companion]", "article", "main section:first-of-type", "main"],
    },
    {
      id: "wn-home-hero-router",
      runtimeSlotId: "ai-product-matcher",
      surface: "inline",
      pageTypes: ["home"],
      question: "Not sure where to start? Find the right Wellness Nest product for your goal.",
      quickReplies: ["Energy and focus", "Workout recovery", "Shilajit routine", "Women's wellness"],
      flowId: "wn-homepage-goal-router-v1",
      priority: 50,
      intentTargets: ["product_discovery", "product_fit", "energy_focus", "recovery", "women_wellness"],
      maxImpressionsPerSession: 1,
      anchorSelectors: ["main section:first-of-type", ".shopify-section:first-of-type", "main"],
    },
    {
      id: "wn-shila-buybox-fit",
      runtimeSlotId: "ai-product-matcher",
      surface: "cta_adjacent",
      pageTypes: ["product"],
      productHandles: ["shilasource-himalayan-shilajit-resin"],
      question: "Is pure resin right for your routine?",
      quickReplies: ["Energy", "Focus", "Vitality", "Taste concern"],
      flowId: "wn-shilasource-fit-v1",
      priority: 90,
      intentTargets: ["product_fit", "energy_focus", "recovery", "format_choice", "taste_concern"],
      maxImpressionsPerSession: 1,
      anchorSelectors: ["form[action*='/cart/add']", "[data-product-form]", ".product-form", "main"],
    },
    {
      id: "wn-shila-proof-explainer",
      runtimeSlotId: "ai-proof-explainer",
      surface: "proof",
      pageTypes: ["product"],
      productHandles: ["shilasource-himalayan-shilajit-resin"],
      sectionType: "lab_proof",
      question: "Want the lab report explained in plain English?",
      quickReplies: ["Heavy metals", "Fulvic acid", "COA", "What it proves"],
      flowId: "wn-proof-explainer-v1",
      priority: 80,
      intentTargets: ["lab_proof", "trust_safety"],
      minIntentScore: 0.2,
      maxImpressionsPerSession: 1,
      anchorSelectors: ["main"],
    },
    {
      id: "wn-shila-format-comparator",
      runtimeSlotId: "ai-product-matcher",
      surface: "inline",
      pageTypes: ["product", "collection"],
      productHandles: ["shilasource-himalayan-shilajit-resin"],
      sectionType: "comparison",
      question: "Resin or gummies? Compare what fits your habit.",
      quickReplies: ["Proof", "Taste", "Convenience", "Travel"],
      flowId: "wn-shilajit-format-comparator-v1",
      priority: 75,
      intentTargets: ["format_choice", "taste_concern", "compare_products"],
      maxImpressionsPerSession: 1,
      anchorSelectors: ["main"],
    },
    {
      id: "wn-shila-faq-objection",
      runtimeSlotId: "ai-dynamic-faq",
      surface: "inline",
      pageTypes: ["product"],
      productHandles: ["shilasource-himalayan-shilajit-resin"],
      sectionType: "faq",
      question: "Still have a question before ordering?",
      quickReplies: ["Timeline", "Medication", "Taste", "Guarantee"],
      flowId: "wn-shilasource-objection-resolver-v1",
      priority: 70,
      intentTargets: ["unresolved_objection", "medication_condition", "taste_concern", "shipping_returns"],
      maxImpressionsPerSession: 1,
      anchorSelectors: ["main"],
    },
    {
      id: "wn-shila-routine-timeline",
      runtimeSlotId: "ai-routine-builder",
      surface: "inline",
      pageTypes: ["product"],
      productHandles: ["shilasource-himalayan-shilajit-resin"],
      question: "Want a 7/30/90-day routine expectation guide?",
      quickReplies: ["Energy", "Focus", "Morning", "Not sure"],
      flowId: "wn-shilasource-routine-timeline-v1",
      priority: 65,
      intentTargets: ["routine_requested", "timeline_expectation", "product_fit"],
      maxImpressionsPerSession: 1,
      anchorSelectors: ["main"],
    },
    {
      id: "wn-shila-stack-builder",
      runtimeSlotId: "ai-bundle-recommendation",
      surface: "inline",
      pageTypes: ["product", "cart"],
      productHandles: ["shilasource-himalayan-shilajit-resin"],
      question: "Want a useful add-on for your goal?",
      quickReplies: ["Energy/focus", "Recovery", "Convenience", "Women's wellness"],
      flowId: "wn-shilasource-stack-builder-v1",
      priority: 68,
      intentTargets: ["bundle_builder", "free_shipping_opportunity", "aov", "commercial_intent"],
      maxImpressionsPerSession: 1,
      anchorSelectors: ["main"],
    },
    {
      id: "wn-cart-free-shipping",
      runtimeSlotId: "ai-bundle-recommendation",
      surface: "cart",
      pageTypes: ["cart"],
      question: "Want the best add-on for your goal?",
      quickReplies: ["Energy/focus", "Recovery", "Convenience"],
      flowId: "wn-cart-addon-v1",
      priority: 100,
      intentTargets: ["free_shipping_opportunity", "price_value", "commercial_intent"],
      maxImpressionsPerSession: 1,
      anchorSelectors: ["form[action='/cart']", "main", "body"],
    },
  ],
  flows,
  products: {
    "shilasource-himalayan-shilajit-resin": {
      handle: "shilasource-himalayan-shilajit-resin",
      title: "ShilaSource Himalayan Shilajit Resin",
      url: shilaSourceUrl,
      price: 59,
      format: "resin",
    },
    "himalayan-shilajit-gummies": {
      handle: "himalayan-shilajit-gummies",
      title: "Himalayan Shilajit Gummies",
      url: gummiesUrl,
      price: 59,
      format: "gummies",
    },
    "fusiforce-creatine-gummies": {
      handle: "fusiforce-creatine-gummies",
      title: "FusiForce Creatine Gummies",
      url: fusiForceUrl,
      price: 69,
      format: "gummies",
    },
    menomate: {
      handle: "menomate",
      title: "MenoMate",
      url: menoMateUrl,
      price: 49,
      format: "capsules",
    },
  },
  approvedCopy: {
    safetyCaveat:
      "If you take medication, are pregnant, or have a medical condition, check with a healthcare professional before using any supplement.",
    labProofSummary:
      "The lab proof is useful for checking identity, purity signals, and safety concerns such as heavy metals. It does not prove that a supplement will treat, cure, or guarantee a health outcome.",
    guaranteeSummary:
      "Review Wellness Nest's guarantee and shipping policy before checkout so the value decision is clear.",
    approvedProofSnippets: {
      heavy_metals:
        "Third-party testing helps check safety concerns such as heavy metals. It reduces uncertainty, but it does not prove a guaranteed health result.",
      fulvic:
        "Fulvic acid is a purity and identity signal often discussed with Shilajit. Treat it as product evidence, not as proof of a medical outcome.",
      coa: "A Certificate of Analysis summarizes what a batch was tested for. Shoppers should review the linked report or merchant source before deciding.",
      fake: "Testing and sourcing transparency help shoppers compare ShilaSource with generic Shilajit products and reduce uncertainty about authenticity.",
      limits:
        "Lab proof does not prove treatment, cure, testosterone increase, or guaranteed energy and focus. It is best used to evaluate identity, purity, and safety signals.",
    },
    fallbackProofResponse:
      "This proof should be used to reduce uncertainty about identity, purity, and safety signals. It should not be treated as a medical promise.",
    medicationSafetyResponse:
      "Because supplement suitability depends on your personal situation, shoppers who take medication, are pregnant, or have a medical condition should check with a healthcare professional before use.",
    valueResponse:
      "Use value to decide, not only price: compare format, proof, routine fit, and guarantee before buying. If you mostly want convenience or taste, compare gummies before choosing resin.",
    tasteResponse:
      "Pure resin has a stronger taste and usually fits shoppers who accept mixing it into tea, coffee, or a routine. If taste and convenience are the main concern, compare gummies before choosing.",
    blockedClaims: ["treat", "cure", "diagnose", "replace medication", "guarantee", "increase testosterone", "fix fatigue"],
  },
};
